import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import SyncIcon from "@mui/icons-material/Sync";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import AdminActionConfirmDialog from "../components/AdminActionConfirmDialog";
import MarketGrowthDataTable, {
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import TeamShareFormDialog from "../components/TeamShareFormDialog";
import TeamShareHistoryDialog from "../components/TeamShareHistoryDialog";
import { getEnumLabel } from "../constants/commissionEnums";
import {
  PARTNER_STATUS_LABELS,
  isPartnerActive,
  isPartnerPendingActivation,
} from "../constants/partnerEnums";

const APPROVAL_ACTION_CONFIG = {
  approve: {
    title: "تأیید نماینده",
    description:
      "با تأیید، نماینده فعال می‌شود و پیامک خوش‌آمدگویی برای او ارسال می‌شود.",
    confirmLabel: "تأیید",
    confirmColor: "success",
    targetStatus: "Active",
  },
  reject: {
    title: "رد درخواست نمایندگی",
    description: "آیا این درخواست رد شود؟",
    confirmLabel: "رد",
    confirmColor: "error",
    targetStatus: "Inactive",
  },
};

const formatPartnerOptionLabel = (partner) => {
  const name =
    partner.displayName ||
    partner.DisplayName ||
    partner.mobileNumber ||
    partner.MobileNumber ||
    `کاربر ${partner.userId ?? partner.UserId ?? partner.id}`;
  const code = partner.referralCode || partner.ReferralCode || "—";
  return `${name} (${code})`;
};

const getPartnerLevel = (partner) =>
  partner?.partnerLevel ?? partner?.PartnerLevel ?? partner?.level ?? partner?.Level;

const isLevel1Partner = (partner) => {
  const level = getPartnerLevel(partner);
  return level === "Level1" || level === 1 || String(level).toLowerCase() === "level1";
};

const isLevel2Partner = (partner) => {
  const level = getPartnerLevel(partner);
  return level === "Level2" || level === 2 || String(level).toLowerCase() === "level2";
};

const MarketGrowthNetworkPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [partners, setPartners] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState("");
  const [teamShares, setTeamShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);

  const [shareDialog, setShareDialog] = useState({ open: false, child: null });
  const [historyDialog, setHistoryDialog] = useState({ open: false, childId: null });
  const [approvalDialog, setApprovalDialog] = useState({ open: false, child: null, action: null });
  const [saving, setSaving] = useState(false);

  const loadPartners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [partnersRes, groupsRes] = await Promise.all([
        MarketGrowthAdminApi.getPartners(),
        MarketGrowthAdminApi.getGroups(),
      ]);
      const partnersNorm = MarketGrowthAdminApi.normalizeList(
        partnersRes,
        "بارگذاری نمایندگان ناموفق بود."
      );
      const groupsNorm = MarketGrowthAdminApi.normalizeList(
        groupsRes,
        "بارگذاری گروه‌ها ناموفق بود."
      );
      if (partnersNorm.error) {
        setError(partnersNorm.error);
        setPartners([]);
        return;
      }
      setPartners(
        Array.isArray(partnersNorm.data)
          ? partnersNorm.data.map(MarketGrowthAdminApi.normalizePartnerProfile)
          : []
      );
      setGroups(Array.isArray(groupsNorm.data) ? groupsNorm.data : []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTeamShares = useCallback(async (parentId) => {
    if (!parentId) {
      setTeamShares([]);
      return;
    }
    setSharesLoading(true);
    try {
      const res = await MarketGrowthAdminApi.getTeamSharesByParent(parentId);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری سهم‌های تیم ناموفق بود."
      );
      setTeamShares(normalized.error || !Array.isArray(normalized.data) ? [] : normalized.data);
    } catch {
      setTeamShares([]);
    } finally {
      setSharesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    loadTeamShares(selectedParentId);
  }, [selectedParentId, loadTeamShares]);

  const groupMap = useMemo(
    () =>
      groups.reduce((acc, g) => {
        acc[g.id] = g.title;
        return acc;
      }, {}),
    [groups]
  );

  const level1Partners = useMemo(
    () => partners.filter(isLevel1Partner),
    [partners]
  );

  const activeShareByChild = useMemo(() => {
    const map = {};
    teamShares.forEach((share) => {
      if (share.isActive && !map[share.childPartnerId]) {
        map[share.childPartnerId] = share.sharePercentOfParentCommission;
      }
    });
    return map;
  }, [teamShares]);

  const tableRows = useMemo(() => {
    if (!selectedParentId) return [];
    return partners
      .filter(
        (p) =>
          isLevel2Partner(p) &&
          Number(p.parentPartnerId ?? p.ParentPartnerId) === Number(selectedParentId)
      )
      .map((child) => ({
        ...child,
        currentSharePercent: activeShareByChild[child.id] ?? null,
        shareLabel:
          activeShareByChild[child.id] != null ? activeShareByChild[child.id] : "—",
        statusLabel: getEnumLabel(PARTNER_STATUS_LABELS, child.status),
        groupTitle: groupMap[child.commissionGroupId] || String(child.commissionGroupId),
        userLabel: child.displayName || String(child.userId),
        inactiveFlag: !isPartnerActive(child.status) ? "غیرفعال" : "",
      }));
  }, [activeShareByChild, groupMap, partners, selectedParentId]);

  const headCells = useMemo(
    () => [
      { id: "id", label: "شناسه" },
      { id: "userLabel", label: "کاربر" },
      { id: "statusLabel", label: "وضعیت", type: "chip" },
      { id: "groupTitle", label: "گروه پورسانتی" },
      { id: "shareLabel", label: "سهم فعلی (%)" },
      { id: "referralCode", label: "کد معرف" },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <CheckIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setApprovalDialog({ open: true, child: row, action: "approve" }),
        color: "success",
        tooltip: "تأیید",
        hidden: (row) => !isPartnerPendingActivation(row.status),
      },
      {
        icon: <CloseIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setApprovalDialog({ open: true, child: row, action: "reject" }),
        color: "error",
        tooltip: "رد",
        hidden: (row) => !isPartnerPendingActivation(row.status),
      },
      {
        icon: <TuneIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setShareDialog({ open: true, child: row }),
        color: "primary",
        tooltip: "تنظیم سهم",
      },
      {
        icon: <HistoryIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setHistoryDialog({ open: true, childId: row.id }),
        color: "secondary",
        tooltip: "تاریخچه سهم",
      },
    ],
    []
  );

  const handleApprovalConfirm = async () => {
    const { child, action } = approvalDialog;
    const config = APPROVAL_ACTION_CONFIG[action];
    if (!child || !config) return;

    setSaving(true);
    try {
      const res = await MarketGrowthAdminApi.patchPartnerStatus(child.id, config.targetStatus);
      const normalized = MarketGrowthAdminApi.normalize(res, "عملیات ناموفق بود.");
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      toast.success(
        action === "approve"
          ? "نماینده تأیید شد. پیامک اطلاع‌رسانی ارسال می‌شود."
          : "درخواست نمایندگی رد شد."
      );
      setApprovalDialog({ open: false, child: null, action: null });
      await Promise.all([loadPartners(), loadTeamShares(selectedParentId)]);
    } catch (e) {
      toast.error(e?.message || "خطا در انجام عملیات");
    } finally {
      setSaving(false);
    }
  };

  const handleShareSubmit = async (payload) => {
    setSaving(true);
    try {
      const res = await MarketGrowthAdminApi.createTeamShare(payload);
      const normalized = MarketGrowthAdminApi.normalize(
        res,
        "ثبت سهم پورسانت ناموفق بود."
      );
      if (normalized.error) return { error: normalized.error };
      toast.success("سهم پورسانت با موفقیت ثبت شد.");
      setShareDialog({ open: false, child: null });
      await loadTeamShares(selectedParentId);
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  return (
    <MarketGrowthPageShell
      title="شبکه نمایندگان"
      description="مدیریت تیم و سهم پورسانت نمایندگان سطح ۲."
      loading={loading}
      error={error}
      onRetry={loadPartners}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>مدیر فروش</InputLabel>
          <Select
            value={selectedParentId}
            label="مدیر فروش"
            onChange={(e) => setSelectedParentId(e.target.value)}
          >
            <MenuItem value="">انتخاب کنید...</MenuItem>
            {level1Partners.map((partner) => (
              <MenuItem key={partner.id} value={partner.id}>
                {formatPartnerOptionLabel(partner)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title="بارگذاری مجدد">
          <IconButton onClick={loadPartners}>
            <SyncIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {!selectedParentId ? (
        <Alert severity="info">یک مدیر فروش انتخاب کنید تا اعضای تیم نمایش داده شوند.</Alert>
      ) : sharesLoading ? (
        <Alert severity="info">در حال بارگذاری اطلاعات تیم...</Alert>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <MarketGrowthDataTable
              rowKey="id"
              rows={tableRows}
              headCells={headCells}
              actions={tableActions}
              defaultSort={{ orderBy: "id", order: "desc" }}
            />
          </CardContent>
        </Card>
      )}

      <TeamShareFormDialog
        open={shareDialog.open}
        onClose={() => !saving && setShareDialog({ open: false, child: null })}
        parentPartnerId={selectedParentId}
        childPartner={shareDialog.child}
        onSubmit={handleShareSubmit}
        saving={saving}
      />

      <TeamShareHistoryDialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, childId: null })}
        parentPartnerId={selectedParentId}
        childPartnerId={historyDialog.childId}
      />

      <AdminActionConfirmDialog
        open={approvalDialog.open}
        title={APPROVAL_ACTION_CONFIG[approvalDialog.action]?.title}
        description={APPROVAL_ACTION_CONFIG[approvalDialog.action]?.description}
        confirmLabel={APPROVAL_ACTION_CONFIG[approvalDialog.action]?.confirmLabel}
        confirmColor={APPROVAL_ACTION_CONFIG[approvalDialog.action]?.confirmColor}
        showNotes={false}
        saving={saving}
        onClose={() =>
          !saving && setApprovalDialog({ open: false, child: null, action: null })
        }
        onConfirm={handleApprovalConfirm}
      />
    </MarketGrowthPageShell>
  );
};

export default MarketGrowthNetworkPage;
